import { useState, useEffect, useCallback } from 'react';
import { getStudents, createStudent, deleteStudent, importStudentsCSV, getClasses, getTeacherClasses, updateStudentCode, exportStudentsCSV, exportStudentsPdfIds } from '../lib/api';
import { saveAs } from 'file-saver';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Download, Loader2 } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { ClassesSection } from '../components/students/ClassesSection';
import { ImportCsvDialog, AddStudentDialog } from '../components/students/StudentDialogs';
import { StudentsTable } from '../components/students/StudentsTable';

import { usePageTitle } from '../hooks/usePageTitle';

export default function Students() {
  usePageTitle('Élèves & Classes');
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [teacherClasses, setTeacherClasses] = useState([]);
  const [selectedClasse, setSelectedClasse] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [form, setForm] = useState({ prenom: '', nom: '', classe: '' });
  const [importClasse, setImportClasse] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [editingCode, setEditingCode] = useState(null);

  const effectiveClasse = selectedClasse && selectedClasse !== 'all' ? selectedClasse : '';

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [studRes, classRes, tcRes] = await Promise.all([
        getStudents(effectiveClasse || undefined),
        getClasses(),
        getTeacherClasses()
      ]);
      setStudents(studRes.data.data);
      setClasses(classRes.data);
      setTeacherClasses(tcRes.data);
    } catch { toast.error('Erreur de chargement des données'); }
    setLoading(false);
  }, [effectiveClasse]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAdd = async () => {
    if (!form.prenom || !form.nom || !form.classe) {
      toast.error('Remplissez tous les champs');
      return;
    }
    try {
      await createStudent(form);
      toast.success('Élève ajouté');
      setForm({ prenom: '', nom: '', classe: '' });
      setShowAdd(false);
      loadData();
    } catch (e) {
      toast.error("Erreur lors de l'ajout");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteStudent(id);
      toast.success('Élève supprimé');
      loadData();
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSaveCode = async (studentId) => {
    if (!editingCode || editingCode.studentId !== studentId) return;
    try {
      await updateStudentCode(studentId, editingCode.code);
      toast.success('Identifiant mis à jour');
      setEditingCode(null);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const [csvLoading, setCsvLoading] = useState(false);

  const handleExportCSV = async () => {
    setCsvLoading(true);
    try {
      const res = await exportStudentsCSV(effectiveClasse || undefined);
      saveAs(new Blob([res.data], { type: 'text/csv;charset=utf-8' }), `eleves${effectiveClasse ? '_' + effectiveClasse : ''}.csv`);
    } catch (e) {
      toast.error('Erreur lors de l\'export CSV');
    }
    setCsvLoading(false);
  };

  const [pdfLoading, setPdfLoading] = useState(false);

  const handleExportPdfIds = async () => {
    if (!effectiveClasse) {
      toast.error('Veuillez sélectionner une classe pour générer le PDF.');
      return;
    }
    setPdfLoading(true);
    try {
      const res = await exportStudentsPdfIds(effectiveClasse);
      saveAs(new Blob([res.data], { type: 'application/pdf' }), `liste-id-eleves-${effectiveClasse.replace(/\s/g, '-')}.pdf`);
    } catch (e) {
      let msg = 'Erreur lors de la génération du PDF';
      if (e.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const parsed = JSON.parse(text);
          msg = parsed.detail || msg;
        } catch (_) {}
      } else if (e.response?.data?.detail) {
        msg = e.response.data.detail;
      }
      toast.error(msg);
    }
    setPdfLoading(false);
  };

  const handleImport = async () => {
    if (!importFile || !importClasse) {
      toast.error('Sélectionnez un fichier et une classe');
      return;
    }
    try {
      const res = await importStudentsCSV(importFile, importClasse);
      toast.success(`${res.data.imported} élèves importés`);
      setShowImport(false);
      setImportFile(null);
      setImportClasse('');
      loadData();
    } catch (e) {
      toast.error("Erreur lors de l'import");
    }
  };

  const dropzone = useDropzone({
    accept: { 'text/csv': ['.csv'] },
    maxFiles: 1,
    onDrop: (files) => setImportFile(files[0])
  });

  const downloadCsvTemplate = () => {
    const csv = 'prenom,nom\nJean,Dupont\nMarie,Martin\nLucas,Bernard\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'modele_eleves.csv';
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const niveauLabel = (nv) => ({ 'college': 'Collège', 'lycee': 'Lycée', 'postbac': 'Post-Bac' }[nv] || nv);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="students-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight">Élèves & Classes</h1>
          <p className="text-muted-foreground mt-1">Gérez vos classes et listes d'élèves.</p>
        </div>
      </div>

      <ClassesSection teacherClasses={teacherClasses} onRefresh={loadData} />

      {/* ===== STUDENTS SECTION ===== */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h2 className="text-lg font-bold">Liste des élèves</h2>
        <div className="flex gap-3">
          <ImportCsvDialog open={showImport} onOpenChange={setShowImport} teacherClasses={teacherClasses} importClasse={importClasse} setImportClasse={setImportClasse}
            importFile={importFile} dropzone={dropzone} onDownloadTemplate={downloadCsvTemplate} onImport={handleImport} />
          <AddStudentDialog open={showAdd} onOpenChange={setShowAdd} teacherClasses={teacherClasses} form={form} setForm={setForm} onAdd={handleAdd} />
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-4 items-center">
        <Select value={selectedClasse} onValueChange={setSelectedClasse}>
          <SelectTrigger className="w-48" data-testid="filter-classe">
            <SelectValue placeholder="Toutes les classes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les classes</SelectItem>
            {classes.map((c) => (
              <SelectItem key={c.classe} value={c.classe}>{c.classe} ({c.count})</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">{students.length} élèves</span>
        {students.length > 0 && (
          <>
            <Button variant="outline" size="sm" onClick={handleExportCSV} disabled={csvLoading} data-testid="export-students-csv">
              {csvLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />} Exporter CSV
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportPdfIds} disabled={!effectiveClasse || pdfLoading} data-testid="export-students-pdf-ids" title={!effectiveClasse ? 'Sélectionnez une classe' : ''}>
              {pdfLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />} Liste des ID élèves
            </Button>
          </>
        )}
      </div>

      <StudentsTable students={students} editingCode={editingCode} setEditingCode={setEditingCode} onSaveCode={handleSaveCode} onDelete={handleDelete} />
    </div>
  );
}
